import type { Platform } from './identity';

/**
 * Authentication error codes and helpers
 *
 * These codes are returned by the API on 401 responses.
 * Frontend uses these to determine appropriate action.
 */

// =============================================================================
// JWT Timing Constants (shared between backend and frontend)
// =============================================================================

/** JWT token lifetime: 30 days (matches Lark refresh_token lifetime) */
export const JWT_EXPIRATION_MS = 30 * 24 * 60 * 60 * 1000;

/** Refresh buffer: refresh JWT when 15 days remain */
export const JWT_REFRESH_BUFFER_MS = 15 * 24 * 60 * 60 * 1000;

/**
 * JWT lifetime per sign-in platform (design §5.1).
 *
 * Lark keeps its 30 days: Lark's own refresh token is what revalidates it, unchanged.
 * Every other platform gets 24 hours, because its refresh re-proves the account with
 * the provider (a person disabled in Entra is out within a day) and its server session
 * can be revoked — a 30-day token would outlive both.
 */
export const JWT_EXPIRATION_MS_BY_PLATFORM: Record<Platform, number> = {
  lark: JWT_EXPIRATION_MS,
  microsoft: 24 * 60 * 60 * 1000,
  google: 24 * 60 * 60 * 1000,
  email: 24 * 60 * 60 * 1000,
};

/**
 * An email sign-in has no provider to re-prove it, so its server session ends this
 * long after it began, however often the JWT is refreshed.
 */
export const EMAIL_SESSION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * How long a Microsoft session's admin answer may be reused on an admin route before
 * it is asked again (decision §11.1). Lark is asked every request and never uses it;
 * money and credentials routes ignore it (`requireFreshAdmin`).
 */
export const ADMIN_RECHECK_MS = 10 * 60 * 1000;

/**
 * How recent a client app's token must be for an action that spends money or hands out
 * credentials (requireFreshAdmin). A client session has no refresh token to re-ask
 * Microsoft with: its admin answer is the `roles`/`wids` of the token it was exchanged
 * from, dated by that token's `iat` — and NAA hands out cached tokens up to an hour old.
 * Past this, the client gets FRESH_TOKEN_REQUIRED, fetches a new token and exchanges it.
 */
export const CLIENT_FRESH_ADMIN_MS = 60 * 1000;

/**
 * When to refresh a JWT that lives `lifetimeMs`: half way through.
 *
 * A fixed 15-day buffer on a 24-hour token would refresh on every visibility change;
 * half the lifetime gives the same 15 days for Lark's 30-day token
 * (JWT_REFRESH_BUFFER_MS) and 12 hours for the others.
 */
export function refreshBufferFor(lifetimeMs: number): number {
  return Math.max(0, Math.floor(lifetimeMs / 2));
}

/** Base OAuth scopes required by the app (no offline_access — used by company portal H5 JSSDK) */
export const LARK_OAUTH_SCOPES_BASE = ['bitable:app', 'contact:user.base:readonly', 'drive:drive'] as const;

/** OAuth scopes for extension (includes offline_access for refresh tokens) */
export const LARK_OAUTH_SCOPES = [...LARK_OAUTH_SCOPES_BASE, 'offline_access'] as const;

/**
 * Lark's browser authorization screen.
 *
 * `accounts.larksuite.com`, not the `open.larksuite.com` host the rest of the
 * API uses — the authorize step is a page a person looks at, and it lives on
 * the accounts domain. (Feishu's equivalent is accounts.feishu.cn; this
 * codebase targets Lark Suite throughout.)
 */
export const LARK_OAUTH_AUTHORIZE_URL = 'https://accounts.larksuite.com/open-apis/authen/v1/authorize';

/**
 * Build the URL that starts a browser OAuth login.
 *
 * Used only outside the Lark client. Inside it, `tt.requestAccess()` obtains
 * the same kind of code silently, and the code both paths produce is redeemed
 * by the same POST /api/auth/login.
 *
 * `redirectUri` must match the value later sent to /api/auth/login *exactly* —
 * Lark compares them when redeeming the code — and must be registered as a
 * redirect URL on the app in the Lark developer console.
 */
export function buildLarkAuthorizeUrl(params: {
  appId: string;
  redirectUri: string;
  /** Opaque anti-CSRF value; echoed back unchanged on the redirect. */
  state: string;
  scopes?: readonly string[];
}): string {
  const url = new URL(LARK_OAUTH_AUTHORIZE_URL);
  // `client_id`, NOT `app_id`. This endpoint takes the OAuth 2.0 spelling; given
  // `app_id` it fails to resolve the app at all and answers with error 20027,
  // listing every requested scope as "missing" — including the ones the app
  // plainly holds, which makes it read like a permissions problem rather than a
  // malformed request. `response_type` is likewise omitted: this is the exact
  // parameter set the extension's browser login has been using successfully.
  url.searchParams.set('client_id', params.appId);
  url.searchParams.set('redirect_uri', params.redirectUri);
  url.searchParams.set('scope', (params.scopes ?? LARK_OAUTH_SCOPES).join(' '));
  url.searchParams.set('state', params.state);
  return url.toString();
}

/**
 * JWT-related error codes (middleware-level, any authenticated endpoint)
 */
export const JWT_ERROR_CODES = {
  /** No Authorization header provided */
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  /** JWT signature is invalid or malformed */
  TOKEN_INVALID: 'TOKEN_INVALID',
  /** JWT has expired (can attempt refresh) */
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  /** Refresh token has expired */
  REFRESH_EXPIRED: 'REFRESH_EXPIRED',
  /** User not found in database */
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  /** Tenant not found in database */
  TENANT_NOT_FOUND: 'TENANT_NOT_FOUND',
} as const

/**
 * OAuth-related error codes (handler-level, specific endpoints)
 */
export const OAUTH_ERROR_CODES = {
  /** User's Lark OAuth token has expired */
  OAUTH_EXPIRED: 'OAUTH_EXPIRED',
} as const

/**
 * Tenant/App authorization error codes (Lark API level)
 */
export const TENANT_ERROR_CODES = {
  /** App is not authorized/installed for the tenant (Lark code 10013) */
  APP_UNAUTHORIZED: 'APP_UNAUTHORIZED',
  /** User does not have permission to use this app (Lark code 20010) */
  USER_UNAUTHORIZED: 'USER_UNAUTHORIZED',
} as const

/**
 * Accounts beyond Lark (design §5.1). Which status each comes with is noted; only
 * SESSION_REVOKED is a 401 that a frontend must treat as "sign in again".
 */
export const IDENTITY_ERROR_CODES = {
  /** 403: this route serves another platform's sessions (a Microsoft session on a Lark-only route). */
  PLATFORM_MISMATCH: 'PLATFORM_MISMATCH',
  /** 404: the platform is not switched on in this environment (IDENTITY_PLATFORMS). */
  PLATFORM_DISABLED: 'PLATFORM_DISABLED',
  /** 401: the server session behind the JWT was signed out, or its person removed. */
  SESSION_REVOKED: 'SESSION_REVOKED',
  /** 403: the customer's Microsoft 365 admin must consent first (AADSTS65001/90094). */
  MS_CONSENT_REQUIRED: 'MS_CONSENT_REQUIRED',
  /**
   * 403: a personal Microsoft account the sign-in could not place (no address in its token).
   * A personal account with an address signs in to its email account, like a personal Google one.
   */
  PERSONAL_ACCOUNT: 'PERSONAL_ACCOUNT',
  /**
   * 403: a personal Google or Microsoft account named after an address the provider does
   * not run (ops@acme.com, not @gmail.com / @outlook.com) signs in to that address's email
   * account only once the address has been proven by emailed code and this provider
   * account connected by that person (`providerOwnsAddress`). The person signs in with
   * the emailed code instead.
   */
  EMAIL_CODE_REQUIRED: 'EMAIL_CODE_REQUIRED',
  /** 403: SharePoint and Dataverse do not exist for a personal Microsoft account; only files do. */
  MS_PERSONAL_UNSUPPORTED: 'MS_PERSONAL_UNSUPPORTED',
  /**
   * 403: the account admits people from its organisation only after an admin approves
   * (join policy 'approval'). The request is recorded; no session is opened. The body
   * carries `accountName`.
   */
  JOIN_REQUESTED: 'JOIN_REQUESTED',
  /** 403: the invitation is for an account of another organisation than the one signed in with. */
  INVITATION_WRONG_ORGANISATION: 'INVITATION_WRONG_ORGANISATION',
  /** 400: the invitation is unknown, expired, withdrawn or used. */
  INVITATION_INVALID: 'INVITATION_INVALID',
  /** 409: the change would leave an account with no admin. */
  LAST_ADMIN: 'LAST_ADMIN',
  /** 403: the person was removed from the account by an admin. */
  MEMBER_REMOVED: 'MEMBER_REMOVED',
  /** 409: the person already belongs to another account (a user belongs to one account). */
  USER_IN_OTHER_ACCOUNT: 'USER_IN_OTHER_ACCOUNT',
  /** 403: the automatic trial is for organisations (decision §11.3). */
  TRIAL_NOT_AVAILABLE: 'TRIAL_NOT_AVAILABLE',
  /**
   * 401: a client app's session (Excel, Word, Sheets, Teams) has no refresh token to
   * re-prove it; the client re-runs its silent token step and exchanges again.
   */
  CLIENT_REAUTH: 'CLIENT_REAUTH',
  /**
   * 401: a money or credentials route needs an admin answer younger than ADMIN_RECHECK_MS
   * and this client session cannot re-derive one itself; the client re-exchanges and retries.
   */
  FRESH_TOKEN_REQUIRED: 'FRESH_TOKEN_REQUIRED',
  /**
   * 401: a token a client app exchanged (Office/Teams NAA, Apps Script identity token)
   * failed verification. One code for every claim failure; the reason is only logged.
   */
  INVALID_CLIENT_TOKEN: 'INVALID_CLIENT_TOKEN',
  /** 400: a server-held OAuth attempt's state is unknown, expired or already used. */
  ATTEMPT_INVALID: 'ATTEMPT_INVALID',
  /** 403: the provider handed back a grant for someone other than the person who started the connection. */
  CONNECTION_USER_MISMATCH: 'CONNECTION_USER_MISMATCH',
  /**
   * 403: a client app asked to connect a provider account it cannot vouch for (the person
   * neither signed in with that provider nor connected it before, and, for Sheets, sent no
   * identity token). The first connection is made from the portal.
   */
  CONNECT_IN_PORTAL: 'CONNECT_IN_PORTAL',
} as const

/**
 * All authentication error codes
 */
export const AUTH_ERROR_CODES = {
  ...JWT_ERROR_CODES,
  ...OAUTH_ERROR_CODES,
  ...TENANT_ERROR_CODES,
  ...IDENTITY_ERROR_CODES,
} as const

export type AuthErrorCode = typeof AUTH_ERROR_CODES[keyof typeof AUTH_ERROR_CODES]

/**
 * Check if this error code can be recovered by refreshing the JWT
 * If true, attempt refresh before giving up
 */
export function canAttemptRefresh(code: string | undefined): boolean {
  return code === 'TOKEN_EXPIRED'
}

/**
 * A refusal from the API's requireFreshAdmin (401 FRESH_TOKEN_REQUIRED): the session is
 * fine, but the admin answer this action needs could not be re-confirmed; the person
 * must sign in again. One test, so every page calling a fresh-admin route says the same.
 */
export function isFreshAdminRefusal(err: unknown): boolean {
  return !!err && typeof err === 'object' && (err as { code?: unknown }).code === 'FRESH_TOKEN_REQUIRED'
}

/**
 * Check if this error code requires clearing the session and re-authenticating
 * These errors cannot be recovered without user action
 */
export function requiresReauth(code: string | undefined): boolean {
  switch (code) {
    case 'TOKEN_EXPIRED':
      return false // Can attempt refresh first
    // requireFreshAdmin: the session is fine, only this action wants a newer admin
    // answer (a client-token Microsoft session past CLIENT_FRESH_ADMIN_MS). Signing the
    // person out would turn a refused action into a lost session.
    case 'FRESH_TOKEN_REQUIRED':
      return false
    case 'AUTH_REQUIRED':
    case 'TOKEN_INVALID':
    case 'REFRESH_EXPIRED':
    case 'USER_NOT_FOUND':
    case 'TENANT_NOT_FOUND':
    case 'OAUTH_EXPIRED':
    case 'APP_UNAUTHORIZED':
    case 'USER_UNAUTHORIZED':
    case 'SESSION_REVOKED':
      return true
    default:
      // Unknown 401 codes default to requiring reauth
      return true
  }
}
